// src/pages/notifications/ContactCreatePage.tsx
// Contact create — desktop-app create dialog (docs/ux/14-forms.md), built on
// the form-kit (FormDialog/TitleInput) from src/components/form. Route-hosted
// (kept for direct links from ContactListPage's "+ New") — the dialog opens
// over the list on mount and closing returns to it.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useNotificationsContact, useI18n } from '@react/hooks';
import { FormDialog, TitleInput } from '@/components/form';
import { Select } from '@/components/Select';
import { Alert } from '@react/components';
import { validateContactForm, formatContactType } from '@/features/notifications/logic';
import { ContactTypeValues } from '@core/types/notifications/ContactType';
import type { ContactFormState } from '@/features/notifications/types';
import { getApiClient } from '@core/api';
import type { ContactResponse } from '@core/types/notifications/ContactResponse';
import type { ValidateContactValueResponse } from '@core/types/notifications/ValidateContactValueResponse';

export const ContactCreatePage = observer(() => {
  const store = useNotificationsContact();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [form, setForm] = useState<ContactFormState>({ type: 'CONTACT_TYPE_UNSPECIFIED', value: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<unknown>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [checkResult, setCheckResult] = useState<{ valid: boolean; message?: string; duplicate?: boolean } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const close = () => navigate('/settings/contacts');

  const dirty = !!form.value.trim();

  const getPlaceholder = () => {
    if (form.type === 'CONTACT_TYPE_EMAIL') return t('notifications.contactValuePlaceholderEmail');
    if (form.type === 'CONTACT_TYPE_PHONE') return t('notifications.contactValuePlaceholderPhone');
    if (form.type === 'CONTACT_TYPE_TELEGRAM') return t('notifications.contactValuePlaceholderTelegram');
    return t('notifications.contactValue');
  };

  const getInputMode = (): 'email' | 'tel' | 'text' => {
    if (form.type === 'CONTACT_TYPE_EMAIL') return 'email';
    if (form.type === 'CONTACT_TYPE_PHONE') return 'tel';
    return 'text';
  };

  // "Check" combines two RPCs before the person commits to creating a
  // contact: ValidateContactValue (format) and GetContactByValue (is this
  // value already registered?). GetContactByValue's generated client drops
  // its query params entirely (a codegen gap — see
  // docs/ux/resource-ia.md's note on the same class of bug) so it's called
  // directly against the client rather than through notificationsApi.
  const handleCheck = async () => {
    if (!form.value.trim() || form.type === 'CONTACT_TYPE_UNSPECIFIED') return;
    setIsChecking(true);
    setCheckResult(null);
    try {
      const validation: ValidateContactValueResponse = await store.ValidateContactValue({
        contact_type: form.type,
        contact_value: form.value.trim(),
      });
      if (!validation.is_valid) {
        setCheckResult({ valid: false, message: validation.validation_message || validation.suggestions?.join(', ') });
        return;
      }
      let duplicate = false;
      try {
        const client = getApiClient();
        const existing = await client.get<ContactResponse>('/notifications/contacts/by-value', {
          params: { contact_type: form.type, contact_value: form.value.trim() },
        });
        duplicate = !!existing.contact;
      } catch {
        // Not found is the expected "no duplicate" outcome — swallow.
      }
      setCheckResult({ valid: true, duplicate });
    } catch (err) {
      setCheckResult({ valid: false, message: err instanceof Error ? err.message : t('errors.unknown') });
    } finally {
      setIsChecking(false);
    }
  };

  const submit = async () => {
    const validation = validateContactForm(form);
    if (Object.keys(validation).length > 0) {
      setErrors(Object.fromEntries(Object.entries(validation).map(([k, v]) => [k, t(v)])));
      return;
    }
    setIsSubmitting(true);
    try {
      await store.CreateContact({
        contact: {
          type: form.type,
          value: form.value.trim(),
        },
      } as never);
      close();
    } catch (err) {
      setFormError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormDialog
      open
      onClose={close}
      onSubmit={submit}
      submitLabel={t('notifications.createContact')}
      canSubmit={!!form.value.trim() && form.type !== 'CONTACT_TYPE_UNSPECIFIED'}
      isSubmitting={isSubmitting}
      dirty={dirty}
      aria-label={t('notifications.createContact')}
      context={
        <Select
          value={form.type}
          onChange={(e) => {
            setForm((p) => ({ ...p, type: e.target.value }));
            setCheckResult(null);
            if (errors.type) setErrors((p) => ({ ...p, type: '' }));
          }}
          fullWidth={false}
          className="w-40"
          aria-label={t('notifications.contactType')}
        >
          <option value="CONTACT_TYPE_UNSPECIFIED">{t('common.select')}</option>
          {ContactTypeValues.filter((v) => v !== 'CONTACT_TYPE_UNSPECIFIED').map((s) => {
            const ct = formatContactType(s);
            return <option key={s} value={s}>{t(ct.labelKey)}</option>;
          })}
        </Select>
      }
    >
      <div className="space-y-3 px-3.5 pb-4 pt-2">
        <TitleInput
          value={form.value}
          onChange={(v) => {
            setForm((p) => ({ ...p, value: v }));
            setCheckResult(null);
            if (errors.value) setErrors((p) => ({ ...p, value: '' }));
          }}
          placeholder={getPlaceholder()}
          initialFocus
          aria-label={t('notifications.contactValue')}
          className={getInputMode() === 'text' ? undefined : 'font-mono'}
        />
        {errors.value && <p className="text-sm text-destructive">{errors.value}</p>}
        {errors.type && <p className="text-sm text-destructive">{errors.type}</p>}

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleCheck}
            disabled={!form.value.trim() || form.type === 'CONTACT_TYPE_UNSPECIFIED' || isChecking}
            className="text-xs text-muted-foreground hover:text-foreground disabled:opacity-40"
          >
            {isChecking ? t('common.loading') : t('notifications.checkContact')}
          </button>
        </div>

        {checkResult && (
          <Alert variant={checkResult.valid ? (checkResult.duplicate ? 'warning' : 'success') : 'error'}>
            {!checkResult.valid
              ? (checkResult.message || t('notifications.contactInvalid'))
              : checkResult.duplicate
                ? t('notifications.contactDuplicate')
                : t('notifications.contactValid')}
          </Alert>
        )}

        {formError != null && (
          <p className="text-sm text-destructive">{formError instanceof Error ? formError.message : t('notifications.saveError')}</p>
        )}
      </div>
    </FormDialog>
  );
});

export default ContactCreatePage;
